import { SEARCH_DEBOUNCE_MS, SEARCH_MIN_CHARS, type SearchResponse } from '@palaccount/contracts';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import * as React from 'react';

import { api } from '@/api';
import { throwApiError } from '@/lib/server-messages';

/** GEN-12's data layer. */

const KEY = ['search'] as const;

/**
 * GEN-12b, both halves: *"The panel must open at the second typed character
 * and must wait 250 milliseconds after typing stops before asking the
 * server."*
 *
 * The debounce is here rather than in the panel because it is a rule about
 * ASKING THE SERVER, not about drawing: the panel opens on the second
 * character immediately (that is the other half of the same rule) and shows
 * its searching state, while the request itself waits out the pause. Putting
 * the delay in the component would have made the two happen together and the
 * panel would flicker open a quarter-second late on every word.
 */
export function useDebounced<T>(value: T, ms: number = SEARCH_DEBOUNCE_MS): T {
  const [settled, setSettled] = React.useState(value);
  React.useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

/** Below two characters there is nothing to ask (GEN-12: "after 2 typed characters"). */
export function isSearchable(q: string): boolean {
  return q.trim().length >= SEARCH_MIN_CHARS;
}

/**
 * One page of grouped results.
 *
 * `keepPreviousData` is deliberate and is a legibility decision rather than a
 * performance one: without it every settled keystroke empties the panel to
 * its loading state and the reader loses the rows they were halfway through
 * reading. With it, the previous answer stays on screen — marked stale by
 * `isFetching`, which is what draws §4.3.2's shimmer — until the new one
 * arrives.
 */
export function useGlobalSearch(q: string, perGroup?: number) {
  const debounced = useDebounced(q);
  const enabled = isSearchable(debounced);

  return useQuery({
    queryKey: [...KEY, debounced.trim(), perGroup ?? 'panel'],
    queryFn: async (): Promise<SearchResponse> => {
      const res = await api.search.query({
        query: {
          q: debounced.trim(),
          ...(perGroup === undefined ? {} : { perGroup }),
        },
      });
      if (res.status !== 200) throwApiError(res.status, res.body);
      return res.body;
    },
    enabled,
    placeholderData: keepPreviousData,
    // A search is a read of live data a person is about to act on, but two
    // keystrokes apart is not two different questions: a short window makes
    // backspacing instant without ever showing a stale row for long.
    staleTime: 30_000,
    retry: 1,
  });
}
